import React, { useState } from "react";
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import SearchBar from '../SearchBar/SearchBar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { userDisplayName, userInitial } from '../../utils/userDisplayName';

import "./NavBar.css";

const nhlTeamLogoUrl = (abbr) =>
    `https://assets.nhle.com/logos/nhl/svg/${abbr}_dark.svg`;

function TeamNavRow({ team, compact = false }) {
    const logo = compact ? 20 : 26;
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: compact ? 0.6 : 1,
                width: '100%',
                minWidth: 0,
            }}
        >
            <Box
                component="img"
                src={nhlTeamLogoUrl(team.abbr)}
                alt={`${team.name} logo`}
                sx={{ width: logo, height: logo, objectFit: 'contain', flexShrink: 0 }}
            />
            <Typography
                component="span"
                sx={{
                    fontSize: compact ? '0.72rem' : '0.8125rem',
                    lineHeight: 1.15,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {team.name}
            </Typography>
        </Box>
    );
}

const prospectsPages = [
    { title: 'Consensus Rankings', path: 'consensus-rankings' },
    { title: 'All Prospects', path: 'players' },
    { title: 'Prospect Comparison', path: 'player-comparison' },
];

const scoutingPages = [
    { title: 'Media Rankings', path: 'media-rankings' },
    { title: 'Independent Rankings', path: 'independent-rankings' },
    { title: 'Blog', path: 'blog' },
];

const yourRankingsPages = [
    { title: 'Your Rankings', path: 'your-rankings' },
    { title: 'Create Ranking', path: 'your-rankings/create', accent: true },
];

// Menus render in a MUI portal (outside .nav-bar), so accent styles must use sx.
const createRankingMenuItemSx = {
    mx: 1.25,
    my: 0.5,
    borderRadius: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: '#a8d47f',
    fontWeight: 600,
    bgcolor: 'rgba(126, 168, 98, 0.18)',
    border: '1px solid rgba(126, 168, 98, 0.45)',
    '& .create-ranking-plus': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        borderRadius: '50%',
        bgcolor: 'rgba(126, 168, 98, 0.35)',
        border: '1px solid currentColor',
        flexShrink: 0,
    },
    '&:hover': {
        bgcolor: 'rgba(126, 168, 98, 0.28)',
        borderColor: 'rgba(148, 192, 115, 0.6)',
        color: '#b8de8f',
    },
    '&.active': {
        color: '#0b0d10',
        background: 'linear-gradient(145deg, #94c073, #7ea862)',
        borderColor: 'rgba(148, 192, 115, 0.7)',
    },
    '&.active .create-ranking-plus': {
        bgcolor: 'rgba(11, 13, 16, 0.12)',
    },
};

function YourRankingsMenuLabel({ title, accent, center = false }) {
    if (!accent) {
        return center ? (
            <Typography textAlign="center">{title}</Typography>
        ) : (
            title
        );
    }

    const content = (
        <>
            <Box className="create-ranking-plus">
                <AddIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography component="span" sx={{ fontWeight: 'inherit' }}>
                {title}
            </Typography>
        </>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                justifyContent: center ? 'center' : 'flex-start',
            }}
        >
            {content}
        </Box>
    );
}

const teamGroups = [
    {
        division: 'Atlantic',
        teams: [
            { id: 'boston_bruins', name: 'Boston Bruins', abbr: 'BOS' },
            { id: 'buffalo_sabres', name: 'Buffalo Sabres', abbr: 'BUF' },
            { id: 'detroit_red_wings', name: 'Detroit Red Wings', abbr: 'DET' },
            { id: 'florida_panthers', name: 'Florida Panthers', abbr: 'FLA' },
            { id: 'montreal_canadiens', name: 'Montreal Canadiens', abbr: 'MTL' },
            { id: 'ottawa_senators', name: 'Ottawa Senators', abbr: 'OTT' },
            { id: 'tampa_bay_lightning', name: 'Tampa Bay Lightning', abbr: 'TBL' },
            { id: 'toronto_maple_leafs', name: 'Toronto Maple Leafs', abbr: 'TOR' },
        ],
    },
    {
        division: 'Metropolitan',
        teams: [
            { id: 'carolina_hurricanes', name: 'Carolina Hurricanes', abbr: 'CAR' },
            { id: 'columbus_blue_jackets', name: 'Columbus Blue Jackets', abbr: 'CBJ' },
            { id: 'new_jersey_devils', name: 'New Jersey Devils', abbr: 'NJD' },
            { id: 'new_york_islanders', name: 'New York Islanders', abbr: 'NYI' },
            { id: 'new_york_rangers', name: 'New York Rangers', abbr: 'NYR' },
            { id: 'philadelphia_flyers', name: 'Philadelphia Flyers', abbr: 'PHI' },
            { id: 'pittsburgh_penguins', name: 'Pittsburgh Penguins', abbr: 'PIT' },
            { id: 'washington_capitals', name: 'Washington Capitals', abbr: 'WSH' },
        ],
    },
    {
        division: 'Central',
        teams: [
            { id: 'chicago_blackhawks', name: 'Chicago Blackhawks', abbr: 'CHI' },
            { id: 'colorado_avalanche', name: 'Colorado Avalanche', abbr: 'COL' },
            { id: 'dallas_stars', name: 'Dallas Stars', abbr: 'DAL' },
            { id: 'minnesota_wild', name: 'Minnesota Wild', abbr: 'MIN' },
            { id: 'nashville_predators', name: 'Nashville Predators', abbr: 'NSH' },
            { id: 'st_louis_blues', name: 'St. Louis Blues', abbr: 'STL' },
            { id: 'winnipeg_jets', name: 'Winnipeg Jets', abbr: 'WPG' },
        ],
    },
    {
        division: 'Pacific',
        teams: [
            { id: 'anaheim_ducks', name: 'Anaheim Ducks', abbr: 'ANA' },
            { id: 'arizona_coyotes', name: 'Arizona Coyotes', abbr: 'ARI' },
            { id: 'calgary_flames', name: 'Calgary Flames', abbr: 'CGY' },
            { id: 'edmonton_oilers', name: 'Edmonton Oilers', abbr: 'EDM' },
            { id: 'los_angeles_kings', name: 'Los Angeles Kings', abbr: 'LAK' },
            { id: 'san_jose_sharks', name: 'San Jose Sharks', abbr: 'SJS' },
            { id: 'seattle_kraken', name: 'Seattle Kraken', abbr: 'SEA' },
            { id: 'vancouver_canucks', name: 'Vancouver Canucks', abbr: 'VAN' },
            { id: 'vegas_golden_knights', name: 'Vegas Golden Knights', abbr: 'VGK' },
        ],
    },
];

function NavBar() {
    const [anchorElNav, setAnchorElNav] = useState("");
    const [anchorElUser, setAnchorElUser] = useState(null);
    const [anchorElProspects, setAnchorElProspects] = useState(null);
    const [anchorElScouting, setAnchorElScouting] = useState(null);
    const [anchorElTeams, setAnchorElTeams] = useState(null);
    const [anchorElYourRankings, setAnchorElYourRankings] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
    const handleCloseNavMenu = () => setAnchorElNav(null);
    const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
    const handleCloseUserMenu = () => setAnchorElUser(null);
    const handleOpenProspectsMenu = (event) => setAnchorElProspects(event.currentTarget);
    const handleCloseProspectsMenu = () => setAnchorElProspects(null);
    const handleOpenScoutingMenu = (event) => setAnchorElScouting(event.currentTarget);
    const handleCloseScoutingMenu = () => setAnchorElScouting(null);
    const handleOpenTeamsMenu = (event) => setAnchorElTeams(event.currentTarget);
    const handleCloseTeamsMenu = () => setAnchorElTeams(null);
    const handleOpenYourRankingsMenu = (event) => setAnchorElYourRankings(event.currentTarget);
    const handleCloseYourRankingsMenu = () => setAnchorElYourRankings(null);

    const handleLogout = async () => {
        handleCloseUserMenu();
        await logout();
        navigate('/');
    };

    const isPathActive = (path) => {
        if (!path) return location.pathname === "/";
        return location.pathname === `/${path}`;
    };

    const isDraftResultsActive =
        location.pathname === "/draft-results" || location.pathname === "/draft-order";

    const isGroupActive = (pages) => pages.some((page) => isPathActive(page.path));
    const isTeamsActive = location.pathname.startsWith('/team/');
    const isYourRankingsActive = isGroupActive(yourRankingsPages);

    return (
        <AppBar position="sticky" elevation={0} sx={{ backgroundColor: '#0b0d10' }} className="nav-bar">
            <Container maxWidth={false}>
                <Toolbar disableGutters sx={{ minHeight: { xs: 56, md: 60 } }}>
                    <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                            component="img"
                            sx={{
                                display: { xs: 'flex', md: 'flex' },
                                height: 38,
                                width: 'auto',
                                maxHeight: { xs: 36, md: 40 },
                                position: "relative"
                            }}
                            alt="Hockey Pipeline"
                            src="/logo@4x.png"
                        />
                    </Link>

                    <Box sx={{ flexGrow: 300, justifyContent: "center", display: { s: 'flex', md: 'none' } }}>
                        <SearchBar />
                    </Box>
                    <Box sx={{ flexGrow: 1, justifyContent: "right", display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            size="large"
                            aria-label="open navigation menu"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            keepMounted
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            sx={{ display: { xs: 'block', md: 'none' } }}
                            PaperProps={{ sx: { maxHeight: 520 } }}
                        >
                            <MenuItem
                                component={Link}
                                to="/"
                                onClick={handleCloseNavMenu}
                                className={location.pathname === "/" ? "active" : ""}
                            >
                                <Typography textAlign="center">Home</Typography>
                            </MenuItem>

                            <MenuItem
                                component={Link}
                                to="/draft-results"
                                onClick={handleCloseNavMenu}
                                className={isDraftResultsActive ? "active" : ""}
                            >
                                <Typography textAlign="center">Draft Results</Typography>
                            </MenuItem>

                            <Divider sx={{ borderColor: '#1e2330' }} />
                            <MenuItem disabled sx={{ opacity: 0.8 }}>
                                <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    Prospects
                                </Typography>
                            </MenuItem>
                            {prospectsPages.map((page) => (
                                <MenuItem
                                    key={page.path}
                                    component={Link}
                                    to={`/${page.path}`}
                                    onClick={handleCloseNavMenu}
                                    className={isPathActive(page.path) ? "active" : ""}
                                >
                                    <Typography textAlign="center">{page.title}</Typography>
                                </MenuItem>
                            ))}

                            <Divider sx={{ borderColor: '#1e2330' }} />
                            <MenuItem disabled sx={{ opacity: 0.8 }}>
                                <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    Scouts
                                </Typography>
                            </MenuItem>
                            {scoutingPages.map((page) => (
                                <MenuItem
                                    key={page.path}
                                    component={Link}
                                    to={`/${page.path}`}
                                    onClick={handleCloseNavMenu}
                                    className={isPathActive(page.path) ? "active" : ""}
                                >
                                    <Typography textAlign="center">{page.title}</Typography>
                                </MenuItem>
                            ))}

                            <Divider sx={{ borderColor: '#1e2330' }} />
                            <MenuItem disabled sx={{ opacity: 0.8 }}>
                                <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    Teams
                                </Typography>
                            </MenuItem>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                    columnGap: 0.5,
                                    rowGap: 0,
                                    px: 0.75,
                                    py: 0,
                                    maxHeight: 260,
                                    overflowY: 'auto',
                                }}
                            >
                                {teamGroups.map((group) => (
                                    <React.Fragment key={group.division}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                gridColumn: '1 / -1',
                                                fontSize: 9,
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.07em',
                                                color: '#6b7280',
                                                pt: 0.5,
                                                pb: 0.1,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {group.division}
                                        </Typography>
                                        {group.teams.map((team) => (
                                            <Box
                                                key={team.id}
                                                component={Link}
                                                to={`/team/${team.id}`}
                                                onClick={handleCloseNavMenu}
                                                className={location.pathname === `/team/${team.id}` ? "active" : ""}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    textDecoration: 'none',
                                                    color: 'inherit',
                                                    py: 0.25,
                                                    px: 0.35,
                                                    borderRadius: 0.75,
                                                    minHeight: 30,
                                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                                                }}
                                            >
                                                <TeamNavRow team={team} compact />
                                            </Box>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </Box>

                            {user && (
                                <>
                                    <Divider sx={{ borderColor: '#1e2330' }} />
                                    <MenuItem disabled sx={{ opacity: 0.8 }}>
                                        <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Your Rankings
                                        </Typography>
                                    </MenuItem>
                                    {yourRankingsPages.map((page) => (
                                        <MenuItem
                                            key={page.path}
                                            component={Link}
                                            to={`/${page.path}`}
                                            onClick={handleCloseNavMenu}
                                            className={isPathActive(page.path) ? 'active' : ''}
                                            sx={page.accent ? createRankingMenuItemSx : undefined}
                                        >
                                            <YourRankingsMenuLabel
                                                title={page.title}
                                                accent={page.accent}
                                                center
                                            />
                                        </MenuItem>
                                    ))}
                                </>
                            )}

                            <Divider sx={{ borderColor: '#1e2330' }} />
                            {user ? (
                                <MenuItem onClick={handleLogout} sx={{ color: '#f87171' }}>
                                    <Typography textAlign="center">Sign Out</Typography>
                                </MenuItem>
                            ) : (
                                <MenuItem component={Link} to="/login" onClick={handleCloseNavMenu}>
                                    <Typography textAlign="center">Sign In</Typography>
                                </MenuItem>
                            )}
                        </Menu>
                    </Box>

                    <Box sx={{ flexGrow: 1, justifyContent: "left", display: { xs: 'none', md: 'flex' } }} className="nav-btns">
                        <Button component={Link} to="/" disableRipple className={location.pathname === "/" ? "active" : ""}>
                            Home
                        </Button>

                        <Button
                            component={Link}
                            to="/draft-results"
                            disableRipple
                            className={isDraftResultsActive ? "active" : ""}
                        >
                            Draft Results
                        </Button>

                        <Button
                            disableRipple
                            endIcon={<ExpandMoreIcon />}
                            onClick={handleOpenProspectsMenu}
                            className={isGroupActive(prospectsPages) ? "active" : ""}
                        >
                            Prospects
                        </Button>
                        <Menu
                            anchorEl={anchorElProspects}
                            open={Boolean(anchorElProspects)}
                            onClose={handleCloseProspectsMenu}
                            PaperProps={{
                                sx: {
                                    backgroundColor: '#13161c',
                                    border: '1px solid #1e2330',
                                    borderRadius: 2,
                                    minWidth: 240,
                                    mt: 1,
                                }
                            }}
                        >
                            {prospectsPages.map((page) => (
                                <MenuItem
                                    key={page.path}
                                    component={Link}
                                    to={`/${page.path}`}
                                    onClick={handleCloseProspectsMenu}
                                    className={isPathActive(page.path) ? "active" : ""}
                                >
                                    {page.title}
                                </MenuItem>
                            ))}
                        </Menu>

                        <Button
                            disableRipple
                            endIcon={<ExpandMoreIcon />}
                            onClick={handleOpenScoutingMenu}
                            className={isGroupActive(scoutingPages) ? "active" : ""}
                        >
                            Scouts
                        </Button>
                        <Menu
                            anchorEl={anchorElScouting}
                            open={Boolean(anchorElScouting)}
                            onClose={handleCloseScoutingMenu}
                            PaperProps={{
                                sx: {
                                    backgroundColor: '#13161c',
                                    border: '1px solid #1e2330',
                                    borderRadius: 2,
                                    minWidth: 240,
                                    mt: 1,
                                }
                            }}
                        >
                            {scoutingPages.map((page) => (
                                <MenuItem
                                    key={page.path}
                                    component={Link}
                                    to={`/${page.path}`}
                                    onClick={handleCloseScoutingMenu}
                                    className={isPathActive(page.path) ? "active" : ""}
                                >
                                    {page.title}
                                </MenuItem>
                            ))}
                        </Menu>

                        <Button
                            disableRipple
                            endIcon={<ExpandMoreIcon />}
                            onClick={handleOpenTeamsMenu}
                            className={isTeamsActive ? "active" : ""}
                        >
                            Teams
                        </Button>
                        <Menu
                            anchorEl={anchorElTeams}
                            open={Boolean(anchorElTeams)}
                            onClose={handleCloseTeamsMenu}
                            MenuListProps={{
                                dense: true,
                                sx: {
                                    display: 'block',
                                    columnCount: 3,
                                    columnGap: '8px',
                                    columnFill: 'auto',
                                    py: 0.35,
                                    px: 0.75,
                                    maxHeight: 380,
                                    overflowY: 'auto',
                                    minWidth: 540,
                                },
                            }}
                            PaperProps={{
                                sx: {
                                    backgroundColor: '#13161c',
                                    border: '1px solid #1e2330',
                                    borderRadius: 2,
                                    minWidth: 540,
                                    maxWidth: 640,
                                    mt: 1,
                                }
                            }}
                        >
                            {teamGroups.flatMap((group) => [
                                <ListSubheader
                                    key={`sub-${group.division}`}
                                    disableSticky
                                    sx={{
                                        columnSpan: 'all',
                                        backgroundColor: '#13161c',
                                        color: '#6b7280',
                                        fontSize: 9,
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.07em',
                                        lineHeight: 1.1,
                                        py: 0.2,
                                        mt: 0.1,
                                        mb: 0,
                                    }}
                                >
                                    {group.division}
                                </ListSubheader>,
                                ...group.teams.map((team) => (
                                    <MenuItem
                                        key={team.id}
                                        component={Link}
                                        to={`/team/${team.id}`}
                                        onClick={handleCloseTeamsMenu}
                                        className={location.pathname === `/team/${team.id}` ? "active" : ""}
                                        sx={{
                                            breakInside: 'avoid',
                                            py: 0.2,
                                            px: 0.5,
                                            minHeight: 0,
                                            my: 0,
                                            borderRadius: 0.75,
                                            fontSize: 11,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <TeamNavRow team={team} compact />
                                    </MenuItem>
                                )),
                            ])}
                        </Menu>

                        {user && (
                            <>
                                <Button
                                    disableRipple
                                    endIcon={<ExpandMoreIcon />}
                                    onClick={handleOpenYourRankingsMenu}
                                    className={isYourRankingsActive ? "active" : ""}
                                >
                                    Your Rankings
                                </Button>
                                <Menu
                                    anchorEl={anchorElYourRankings}
                                    open={Boolean(anchorElYourRankings)}
                                    onClose={handleCloseYourRankingsMenu}
                                    PaperProps={{
                                        sx: {
                                            backgroundColor: '#13161c',
                                            border: '1px solid #1e2330',
                                            borderRadius: 2,
                                            minWidth: 240,
                                            mt: 1,
                                        }
                                    }}
                                >
                                    {yourRankingsPages.map((page) => (
                                        <MenuItem
                                            key={page.path}
                                            component={Link}
                                            to={`/${page.path}`}
                                            onClick={handleCloseYourRankingsMenu}
                                            className={isPathActive(page.path) ? 'active' : ''}
                                            sx={page.accent ? createRankingMenuItemSx : undefined}
                                        >
                                            <YourRankingsMenuLabel
                                                title={page.title}
                                                accent={page.accent}
                                            />
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}
                    </Box>

                    <Box sx={{ flexGrow: { md: 0.5, lg: 1.5, xl: 0.25 }, justifyContent: "right", display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                        <SearchBar />
                    </Box>

                    <Box sx={{ flexShrink: 0, display: { xs: 'none', md: 'flex' }, alignItems: 'center', ml: 1.5 }}>
                        {user ? (
                            <>
                                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0.5 }}>
                                    <Avatar
                                        src={user.picture_url || undefined}
                                        alt={userDisplayName(user)}
                                        sx={{ width: 34, height: 34, bgcolor: '#3b82f6', fontSize: 14 }}
                                    >
                                        {!user.picture_url && userInitial(user)}
                                    </Avatar>
                                </IconButton>
                                <Menu
                                    anchorEl={anchorElUser}
                                    open={Boolean(anchorElUser)}
                                    onClose={handleCloseUserMenu}
                                    PaperProps={{
                                        sx: {
                                            backgroundColor: '#13161c',
                                            border: '1px solid #1e2330',
                                            borderRadius: 2,
                                            minWidth: 180,
                                            mt: 1,
                                        }
                                    }}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                >
                                    <Box sx={{ px: 2, py: 1.5 }}>
                                        <Typography sx={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600 }}>
                                            {userDisplayName(user)}
                                        </Typography>
                                        <Typography sx={{ color: '#6b7280', fontSize: 12 }}>
                                            {user.email}
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ borderColor: '#1e2330' }} />
                                    <MenuItem onClick={handleLogout} sx={{ color: '#f87171', fontSize: 14, '&:hover': { backgroundColor: '#1e2330' } }}>
                                        Sign Out
                                    </MenuItem>
                                </Menu>
                            </>
                        ) : (
                            <Button
                                component={Link}
                                to="/login"
                                variant="outlined"
                                size="small"
                                sx={{
                                    color: '#e2e8f0',
                                    borderColor: '#2d3344',
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    px: 2,
                                    '&:hover': { borderColor: '#4a5568', backgroundColor: '#1e2330' },
                                }}
                            >
                                Sign In
                            </Button>
                        )}
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
}

export default NavBar;